import { Box, Button, Heading, Modal, Paragraph } from '@zillow/constellation-10';
import { useMemo } from 'react';
import { ModalType } from '../../../types/ModalType';
import useModal from '../../../hooks/useModal';
import { getFormattedPetDetails } from '../../../utils/renterProfileUtils';
import type { SatelliteWebLandlordGetLatestRenterProfileDto } from '@zg-rentals/ts-satellite-client';

type RenterProfileModalProps = {
  // Renter profile data from LeadsTable
  renterName?: string;
  renterProfile?: SatelliteWebLandlordGetLatestRenterProfileDto;
};

export default function RenterProfileModal({ renterName, renterProfile }: RenterProfileModalProps): JSX.Element {
  const leadDisplayName = renterName || '-';
  const attributes = renterProfile?.attributes;
  const {
    credit,
    desiredParking,
    incomeYearlyRange,
    voucherAmountMonthly,
    leaseLengthMonths,
    moveInDate,
    numBedrooms,
    numOccupants,
    petDetails,
  } = attributes || {};

  const renterProfileAttributes = useMemo(
    () => [
      {
        title: 'Preferred move-in date',
        value: moveInDate,
      },
      {
        title: 'Preferred lease duration',
        value: leaseLengthMonths ? `${leaseLengthMonths} month(s)` : '',
      },
      {
        title: 'Credit score',
        value: credit,
      },
      {
        title: 'Estimated annual income',
        value: incomeYearlyRange,
      },
      {
        title: 'Housing voucher amount',
        value: voucherAmountMonthly,
      },
      {
        title: 'Pets',
        value: petDetails && petDetails[0] ? getFormattedPetDetails(petDetails) : '',
      },
      {
        title: 'Household size',
        value: numOccupants,
      },
      {
        title: 'Number of bedrooms desired',
        value: numBedrooms,
      },
      {
        title: 'Parking needs',
        value: desiredParking,
      },
    ],
    [
      moveInDate,
      leaseLengthMonths,
      credit,
      incomeYearlyRange,
      petDetails,
      numOccupants,
      numBedrooms,
      desiredParking,
      voucherAmountMonthly,
    ],
  );

  const { clearModal, setActiveModal } = useModal();
  const handleClose = () => clearModal();

  const handleMessageClick = () => {
    setActiveModal(ModalType.SendMessageModal);
  };

  return (
    <Modal
      open={true}
      onOpenChange={() => handleClose()}
      size={{
        base: 'fullScreen',
        md: 'md',
      }}
      header={
        <Heading level={2} textStyle="heading-xs">
          Renter profile
        </Heading>
      }
      body={
        <>
          <Paragraph textStyle="body-xs-bold" css={{ color: 'text.subtle' }}>
            Name
          </Paragraph>
          <Paragraph textStyle="body-bold" css={{ textTransform: 'capitalize', paddingBottom: 'tighter' }}>
            {leadDisplayName}
          </Paragraph>

          {renterProfileAttributes.map((attribute) => {
            return (
              <Box key={attribute.title} css={{ paddingBlock: 'tighter' }}>
                <Paragraph textStyle="body-xs" css={{ color: 'text.subtle' }}>
                  {attribute.title}
                </Paragraph>
                <Paragraph textStyle="body-sm">{attribute.value || '-'}</Paragraph>
              </Box>
            );
          })}
        </>
      }
      footer={
        <Button emphasis="outlined" onClick={handleMessageClick} tone="brand">
          Message
        </Button>
      }
    />
  );
}
